<script lang="ts" setup>
import { Modal } from "@/components";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import {
  useTeamCandidatesForWorkspaceQuery,
  useAddWorkspaceSubscriptionMutation,
  useDeleteWorkspaceSubscriptionMutation,
} from "@/graphql/operations";

const emit = defineEmits(["close", "success"]);
const emitClose = () => {
  if (!submitting.value) emit("close");
};

const emitSuccess = () => {
  emit("success");
};

const props = defineProps({
  active: {
    type: Boolean,
  },
  id: {
    type: String,
    required: true,
  },
});

const { data, fetching, stale, error, executeQuery } =
  useTeamCandidatesForWorkspaceQuery({
    variables: {
      id: props.id,
    },
    requestPolicy: "network-only",
  });

const lastSuccessfulData = ref<typeof data.value>();
watch(
  data,
  (value) => {
    if (value) lastSuccessfulData.value = value;
  },
  { immediate: true },
);
const teamCandidates = computed(
  () => lastSuccessfulData.value?.myMemberships?.nodes,
);
const selected = ref<Array<string>>([]);
const initialSelection = ref<Array<string>>([]);
const submitting = ref(false);
const failed = ref(false);
const selectionLoaded = ref(false);
let unmounted = false;
onBeforeUnmount(() => {
  unmounted = true;
});
watch(
  [teamCandidates, fetching, stale, error],
  () => {
    if (
      fetching.value ||
      stale.value ||
      error.value ||
      !data.value ||
      selectionLoaded.value
    )
      return;
    const selection =
      teamCandidates.value
        ?.filter(
          (c) => c!.team!.workspaceSubscriptionsBySubteamId.nodes.length > 0,
        )
        .map((c) => c!.team!.id) ?? [];
    selected.value = [...selection];
    initialSelection.value = [...selection];
    selectionLoaded.value = true;
  },
  { immediate: true },
);
const submitDisabled = computed(
  () =>
    fetching.value ||
    !selectionLoaded.value ||
    submitting.value ||
    (initialSelection.value.length === selected.value.length &&
      selected.value.every((id) => initialSelection.value.includes(id))),
);

const { executeMutation: addWorkspaceSubscription } =
  useAddWorkspaceSubscriptionMutation();
const { executeMutation: deleteWorkspaceSubscription } =
  useDeleteWorkspaceSubscriptionMutation();
const submit = async () => {
  if (submitDisabled.value) return;
  submitting.value = true;
  failed.value = false;
  try {
    // Mutations return no subscription rows. Resolve each attempt from fresh
    // state, including IDs recreated after an earlier partial submission.
    const current = await executeQuery({ requestPolicy: "network-only" });
    if (unmounted) return;
    if (current.error.value || !current.data.value) {
      failed.value = true;
      return;
    }
    const subscriptions = current.data.value.myMemberships?.nodes ?? [];
    initialSelection.value = subscriptions
      .filter(
        (c) => c!.team!.workspaceSubscriptionsBySubteamId.nodes.length > 0,
      )
      .map((c) => c!.team!.id);
    const newSubscriptions = selected.value.filter(
      (id) => !initialSelection.value.includes(id),
    );
    const removedSubscriptions = initialSelection.value.filter(
      (id) => !selected.value.includes(id),
    );
    for (const teamId of newSubscriptions) {
      const result = await addWorkspaceSubscription({
        teamId,
        workspaceId: props.id,
      });
      if (unmounted) return;
      if (result.error) failed.value = true;
      else initialSelection.value.push(teamId);
    }
    for (const teamId of removedSubscriptions) {
      const id = subscriptions.find((c) => c!.team!.id === teamId)?.team
        ?.workspaceSubscriptionsBySubteamId.nodes[0]?.id;
      if (!id) {
        failed.value = true;
        continue;
      }
      const result = await deleteWorkspaceSubscription({ id });
      if (unmounted) return;
      if (result.error) failed.value = true;
      else
        initialSelection.value = initialSelection.value.filter(
          (id) => id !== teamId,
        );
    }
    if (!failed.value) {
      emitSuccess();
      emit("close");
    }
  } catch {
    if (!unmounted) failed.value = true;
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <Modal @close="emitClose" :active="active" :close-disabled="submitting">
    <template #header>
      {{ $t("workspaceDetail.teamSubscriptions.modalHeader") }}
    </template>
    <template #body>
      <p v-if="failed || error" role="alert" class="has-text-danger">
        {{ $t("error") }}
      </p>
      <template v-if="teamCandidates && teamCandidates.length > 0">
        <div class="info">
          {{ $t("workspaceDetail.teamSubscriptions.modalInfo") }}
        </div>
        <div class="items" v-if="!fetching && selectionLoaded">
          <div
            v-for="team in teamCandidates"
            :key="team!.team!.id"
            class="item"
          >
            <div class="left">
              <span class="checkbox_container">
                <input
                  type="checkbox"
                  :disabled="submitting"
                  :id="team!.team!.id"
                  :value="team!.team!.id"
                  v-model="selected"
                />
              </span>
            </div>

            <div class="left">
              {{ team!.team!.name }}
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="info">
          {{ $t("workspaceDetail.teamSubscriptions.noAdminTeams") }}
        </div>
      </template>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          :class="{ button: true, primary: true, disabled: submitDisabled }"
          :disabled="submitDisabled"
          @click="submit"
        >
          {{ $t("submit") }}
        </button>
        <button
          class="button secondary"
          :disabled="submitting"
          @click="emitClose"
        >
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.left {
  float: left;
  height: 38px;
}

.item {
  padding: 5px 0;
  border-bottom: 1px solid var(--classic-border-subtle, #f4f4f4);
  height: 38px;
  line-height: 38px;
  overflow: auto;
}

.item .checkbox_container {
  display: inline-block;
  padding: 0.5em;
  width: 1.4em;
  margin-right: 0.5em;
}

input[type="checkbox"] {
  cursor: pointer;
}

.item.highlighted,
.item:hover {
  background-color: var(--classic-surface-hover, #fafafa);
}

.info {
  font-size: 0.9em;
  margin-bottom: 0.2em;
  font-style: italic;
}
</style>
